import {
  BaseRequestPolicy,
  HttpOperationResponse,
  RequestPolicy,
  RequestPolicyFactory,
  RequestPolicyOptions,
  RestError,
  WebResourceLike,
} from '@azure/core-http';
import { pageDataService } from 'common/utilities/PageDataService';
import TelemetryProvider from 'common/utilities/TelemetryProvider';

// list of exception codes to notify a native app
const webViewExceptionCodes = [
  '1150', // ForbiddenWithWwwAuthenicateHeaderExceptionCode
];

const isWebView = pageDataService.isWebView();

// Threshold: 10 calls per 10 seconds with same url
const MaxApiCalls = 10;
const DurationInMs = 10000; // 10 seconds

const apiCalls: { [url: string]: number } = {};
let publishedSpammingApiCall = false;

function detectExcessiveAPICalls(url: string): boolean {
  apiCalls[url] = apiCalls[url] ?? 0;
  apiCalls[url] += 1;
  setTimeout(() => {
    apiCalls[url] -= 1;
  }, DurationInMs);

  return apiCalls[url] >= MaxApiCalls;
}

function onApiFailure(exceptionCode: string) {
  TelemetryProvider.Instance.trackEvent('onApiFailure', { exceptionCode });

  if (!window.nativeAppApi) {
    return;
  }

  try {
    window.nativeAppApi.onApiFailure(exceptionCode);
  } catch {
    // ignore proxy exception for back-compat
  }
}

export const notifyApiFailure = (exceptionCode: string): void => {
  if (isWebView && webViewExceptionCodes.includes(exceptionCode)) {
    onApiFailure(exceptionCode);
  }
};

export const enforceApiCallRateLimit = async (url: string): Promise<void> => {
  const detected = detectExcessiveAPICalls(url);
  if (!detected) {
    return;
  }

  const error = new Error(`Too many requests ${url}`);
  if (!publishedSpammingApiCall) {
    publishedSpammingApiCall = true;
    TelemetryProvider.Instance.trackException(error, 'TooManyRequestsDetected', { url });
  }

  await new Promise((resolve) => setTimeout(resolve, 100));
  throw error;
};

/**
 * Request policy to surface `exceptionCode` from API response as `code` in {@link RestError}
 * because `core-http`'s default {@link DeserializationPolicy} does not deserialize `exceptionCode`
 * (see https://github.com/Azure/azure-sdk-for-js/blob/191e4ce330acd60e014c13412204b9598870cfa1/sdk/core/core-http/src/policies/deserializationPolicy.ts#L246)
 */
export class ExceptionCodePolicy extends BaseRequestPolicy {
  constructor(nextPolicy: RequestPolicy, requestPolicyOptions: RequestPolicyOptions) {
    super(nextPolicy, requestPolicyOptions);
  }

  async sendRequest(request: WebResourceLike): Promise<HttpOperationResponse> {
    try {
      await enforceApiCallRateLimit(request.url);

      return await this._nextPolicy.sendRequest(request);
    } catch (reason) {
      if (reason instanceof RestError) {
        // If a RestError has been thrown (by DeserializationPolicy) and exceptionCode is available in response
        reason.code ??= reason.response?.parsedBody?.exceptionCode?.toString() ?? undefined;
        // Note that `code` must be a string since Redux will only serialize the error code (`code`) if it is a string
        // (see SerializedError https://redux-toolkit.js.org/api/createAsyncThunk#handling-thunk-errors)

        if (reason.code) {
          notifyApiFailure(reason.code);
        }
      }
      throw reason;
    }
  }
}

/**
 * Create a {@link RequestPolicyFactory} that builds {@link ExceptionCodePolicy} instances
 */
export const exceptionCodePolicy: () => RequestPolicyFactory = () => ({
  create: (nextPolicy: RequestPolicy, options: RequestPolicyOptions) => new ExceptionCodePolicy(nextPolicy, options),
});
